/**
Bind app hotekeys. Assumes global context, not currently safe for multiple
instances. Reloading "works" by overwriting previous bindings.

Hotkeys registered with meta are rebindable: click the key in the hotkeys
table, press a new combo (Esc cancels). Overrides persist to localStorage
and reapply on load; ↺ restores an entry's default.

Status: Active Experiment in Paint Composer, consolidating here.
*/

{ exec: compileTemplate } from jadelet
{ groupBy } from @danielx/lib

{ default: Mousetrap } from mousetrap

// Override default stop callback behavior
try
  Mousetrap.prototype.stopCallback = (_e: Event, element: HTMLElement, combo: string) ->
    // Don't stop for ctrl+key etc. even in textareas
    if combo.match /^(ctrl|alt|meta|option|command)\+/
      return false

    // stop for input, select, textarea, and content editable
    (or)
      element.tagName is 'INPUT'
      element.tagName is 'SELECT'
      element.tagName is 'TEXTAREA'
      element.isContentEditable

HotkeyTable := compileTemplate """
table
  thead
    tr
      th(colspan="2") Hotkeys
  tbody
    @rows
"""
HotkeyRow := compileTemplate """
tr
  td @name
  td.right
    button.reset(click=@reset class=@resetClass title="Reset to default") ↺
    code.key(click=@record class=@keyClass title=@recordTitle) @key
"""
HotkeyCategoryRow := compileTemplate """
tr.hotkey-category
  th(colspan="2") @group
"""

interface HotkeyMeta
  category: string
  name: string

interface HotkeyInfo extends HotkeyMeta
  key: string

interface RegisteredHotkey extends HotkeyInfo
  defaultKey?: string | string[]
  boundKey?: string | string[]
  method?: Function | string

type Constructor<T={}> = new (...args: any[]) => T

displayKey := (key: string | string[]): string ->
  if Array.isArray key then key.join ", " else key

/** Does a binding (or a ", "-separated info string) include this combo? */
keysMatch := (binding: string | string[], combo: string): boolean ->
  keys := if Array.isArray binding then binding else binding.split ", "
  keys.includes combo

MODIFIER_KEYS := ["Control", "Alt", "Shift", "Meta", "OS"]

/** Mousetrap names for KeyboardEvent.key values that differ */
KEY_NAMES: Record<string, string> :=
  " ": "space"
  Escape: "esc"
  ArrowLeft: "left"
  ArrowRight: "right"
  ArrowUp: "up"
  ArrowDown: "down"
  Delete: "del"
  Insert: "ins"
  Backspace: "backspace"
  Enter: "enter"
  Tab: "tab"
  Home: "home"
  End: "end"
  PageUp: "pageup"
  PageDown: "pagedown"
  CapsLock: "capslock"

/** Translate a keydown into a Mousetrap combo string, null for bare modifiers */
comboFromEvent := (e: KeyboardEvent): string | null ->
  return null if MODIFIER_KEYS.includes e.key

  mods: string[] := []
  mods.push "ctrl" if e.ctrlKey
  mods.push "alt" if e.altKey
  mods.push "meta" if e.metaKey

  key .= KEY_NAMES[e.key] ?? e.key
  // punctuation already encodes shift ("?", "+"); everything else keeps it
  unless key.length is 1 and not /[a-z]/i.test key
    mods.push "shift" if e.shiftKey
    key = key.toLowerCase()

  [...mods, key].join "+"

export Hotkeys := <Base extends Constructor<{}>>(S: Base) ->
  class Hotkeys extends S
    #hotkeys: RegisteredHotkey[] := []
    /** every combo this instance has bound, named or not, for cleanup */
    #bindings: (string | string[])[] := []

    /** localStorage key for persisted rebinds; override per app if needed */
    hotkeyStorageKey = "hotkey-overrides"

    hotkey(...args: Parameters<typeof @addHotkey>)
      @addHotkey(...args)

    hotkeyOverrides(): Record<string, string>
      try
        JSON.parse(localStorage.getItem(@hotkeyStorageKey) or "{}")
      catch
        {}

    saveHotkeyOverride(name: string, key: string | null)
      overrides := @hotkeyOverrides()
      if key?
        overrides[name] = key
      else
        delete overrides[name]
      localStorage.setItem @hotkeyStorageKey, JSON.stringify overrides

    bindHotkey(key: string | string[], method: Function | string)
      // Note: Using the global Mousetrap instance; added hotkeys will
      // replace others with the same key. Mousetrap can't remove its own
      // document listeners (https://github.com/ccampbell/mousetrap/pull/427
      // never merged) but per-combo unbind covers cleanup — see
      // unbindHotkey / unbindAllHotkeys
      @#bindings.push key
      Mousetrap.bind key, (e: Event): void =>
        return if e.defaultPrevented
        e.preventDefault()

        if typeof method is "function"
          method.call(@, e)
        else
          //@ts-ignore
          @[method](e)

    addHotkey<K extends keyof this>(key: string | string[], method: Function | K, meta?: HotkeyMeta)
      boundKey .= key
      if meta
        // a persisted rebind replaces the default before the first bind
        override := @hotkeyOverrides()[meta.name]
        boundKey = override if override?

        @#hotkeys.push Object.assign {
          key: displayKey boundKey
          defaultKey: key
          boundKey
          method: method as Function | string
        }, meta

      @bindHotkey boundKey, method as Function | string

    /** Rebind a named hotkey; returns the conflicting entry when refused */
    rebindHotkey(name: string, combo: string): RegisteredHotkey | undefined
      entry := @#hotkeys.find (h) => h.name is name
      return unless entry? and entry.method?

      // refuse combos in use elsewhere, including info-only entries
      conflict := @#hotkeys.find (other) =>
        other !== entry and keysMatch(other.boundKey ?? other.defaultKey ?? other.key, combo)
      return conflict if conflict

      @unbindKey entry.boundKey ?? entry.defaultKey!
      entry.boundKey = combo
      entry.key = combo
      @bindHotkey combo, entry.method!
      @saveHotkeyOverride name,
        if keysMatch(entry.defaultKey!, combo) then null else combo
      return

    /** Unbind a combo and drop it from this instance's cleanup list */
    unbindKey(key: string | string[])
      Mousetrap.unbind key
      i := @#bindings.indexOf key
      @#bindings.splice i, 1 if i >= 0

    /** Remove a named hotkey entirely: unbound and gone from the table */
    unbindHotkey(name: string)
      i := @#hotkeys.findIndex (h) => h.name is name
      return if i < 0
      entry := @#hotkeys[i]
      if entry.method? and (entry.boundKey ?? entry.defaultKey)?
        @unbindKey entry.boundKey ?? entry.defaultKey!
      @#hotkeys.splice i, 1

    /** Unbind everything this instance registered, named or not — the
    instance-level cleanup Mousetrap itself never shipped */
    unbindAllHotkeys()
      @#bindings.forEach (key) => Mousetrap.unbind key
      @#bindings.length = 0
      @#hotkeys.length = 0

    /** Restore a named hotkey's default binding */
    resetHotkey(name: string)
      entry := @#hotkeys.find (h) => h.name is name
      return unless entry? and entry.method? and entry.defaultKey? and entry.boundKey?
      return if entry.boundKey is entry.defaultKey

      @unbindKey entry.boundKey
      entry.boundKey = entry.defaultKey
      entry.key = displayKey entry.defaultKey
      @bindHotkey entry.defaultKey, entry.method!
      @saveHotkeyOverride name, null

    /** Capture the next key combo pressed and rebind the entry; Esc cancels */
    recordHotkey(entry: RegisteredHotkey, el: HTMLElement)
      return unless entry.method?
      el.textContent = "…"

      finish := (text: string) =>
        window.removeEventListener "keydown", onKey, true
        el.textContent = text

      onKey := (e: KeyboardEvent) =>
        e.preventDefault()
        e.stopPropagation()

        if e.key is "Escape" and not (e.ctrlKey or e.altKey or e.metaKey or e.shiftKey)
          return finish entry.key

        combo := comboFromEvent e
        return unless combo?

        conflict := @rebindHotkey entry.name, combo
        if conflict
          finish `in use: ${conflict.name}`
          setTimeout =>
            el.textContent = entry.key
          , 1500
        else
          finish entry.key
          el.closest("tr")?.querySelector(".reset")?.classList.toggle "hidden",
            entry.boundKey is entry.defaultKey

      window.addEventListener "keydown", onKey, true

    // Add info without binding hotkey
    // useful for large batches of similar keys like 0-9 to select a numbered tool
    addHotkeyInfo(info: HotkeyInfo)
      @#hotkeys.push info

    hotkeys()
      @#hotkeys

    hotkeysInfoElement()
      aggregatedHotkeys := groupBy @#hotkeys, .category

      rows := Object.keys(aggregatedHotkeys).reduce (result, group) =>
        entries := aggregatedHotkeys[group]

        result.push HotkeyCategoryRow
          group: group

        return result.concat entries.map (entry) =>
          rebindable := entry.method?

          HotkeyRow
            name: entry.name
            key: entry.key
            keyClass: if rebindable then "rebindable" else ""
            recordTitle:
              if rebindable
                "Click, then press a new key combo (Esc cancels)"
              else ""
            record: (e: MouseEvent) =>
              return unless rebindable
              @recordHotkey entry, e.currentTarget as HTMLElement
            resetClass:
              if rebindable and entry.boundKey !== entry.defaultKey then ""
              else "hidden"
            reset: (e: MouseEvent) =>
              return unless rebindable
              @resetHotkey entry.name
              button := e.currentTarget as HTMLElement
              button.classList.add "hidden"
              codeEl := button.closest("tr")?.querySelector "code.key"
              codeEl.textContent = entry.key if codeEl?
      , [] as Element[]

      return HotkeyTable
        rows: rows

export default Hotkeys
